/** Templates: a frozen GameSpec + bounded tunables + content slots + the
 * proof requirements that admit derived specs. Instantiation applies data
 * overrides only; the result must still validate and prove. */
import type { JsonObject, JsonValue } from '../../json.js';
import type { AnyComposeModule } from '../module.js';
import { validateGameSpec, type GameSpecV1, type ModuleUseV1 } from '../spec.js';
import { applyStructure, validateStructure, type TemplateStructureV1 } from './structure.js';

export interface TunableV1 {
  /** `/archetypes/<kind>/modules/<moduleId>/<field…>` or
   * `/globals/<instanceKey>/<field…>`; array indices are numeric segments. */
  path: string;
  min: number;
  max: number;
  integer?: boolean;
  /** Further paths that always take the same value (the mirrored seat of a
   * versus match): one tunable, set once, written everywhere it applies. */
  mirrors?: string[];
}

export interface TemplateV1 {
  id: string;
  family: string;
  title: string;
  pitch: string;
  /** Plain-language contract the creator reviews and the recommender reads:
   * what the template plays like, whatever a patch changes. */
  disclosure: TemplateDisclosureV1;
  /** Soundtrack direction for the generated music (data; validated by the
   * platform's closed music brief). */
  music: { mood: string; genre: string; tempoBpm: number; instrumentation: string[] };
  spec: GameSpecV1;
  tunables: TunableV1[];
  /** Free-text fields a generator may rewrite (`/title`, `/archetypes/<kind>/role`, …). */
  contentSlots: Array<{ path: string; maxLength: number }>;
  /** Proof obligations beyond completing the objective. */
  requirements: { events: string[] };
  /** Modules whose removal must make the proof fail (stub check). */
  signatureModules: string[];
  /** One native proof per disclosed core verb, in `disclosure.coreVerbs`
   * order: a semantic seat input held early in a fresh match (checked by
   * `verbProofFailures`, as the browser audit observes it) and
   * the independently observable consequence (a payload path that changes,
   * or an emitted event kind). Builds turn these into the desktop + touch
   * interaction scenarios the player audit and native loop drive. */
  verbProofs: VerbProofV1[];
  /** A RETIRED revision (templates/retired.json): no longer offered to new
   * Creates, still resolvable by its id for the games accepted on it. */
  retired?: true;
}

/** A semantic seat input a verb proof holds: a direction or button, or a
 * pointer TAP on the first entity of a template kind (a `pointer` game). */
export type VerbInputV1 = 'left' | 'right' | 'up' | 'down' | 'a' | 'b';

/** How long a verb proof may hold its input, in ms: the bounds of one action
 * in the build's browser interaction plan, which drives each proof as one
 * held key or control (a longer hold made no plan, so a game whose proofs
 * passed every stage failed at synthesis). */
export const VERB_PROOF_MS = Object.freeze({ min: 100, max: 2000 });

/** A verb only possible later in a match (at a ramp's lip, in the air)
 * cannot happen within one hold from the opening: a proof may first play a
 * short LEAD — held inputs in order, each {@link VERB_PROOF_MS}, at most
 * {@link VERB_PROOF_LEAD_MAX} of them — that carries the player into the
 * verb's situation. The verb must then cause its consequence where the lead
 * alone does not. Never in a pointer game (a tap proof has no lead). */
export interface VerbLeadV1 { input: Exclude<VerbInputV1, 'tap'>; ms: number }
export const VERB_PROOF_LEAD_MAX = 3;

/** What is wrong with a verb proof's lead, or null (absent is fine). */
export function verbLeadProblem(lead: unknown, tap: boolean): string | null {
  if (lead === undefined) return null;
  if (tap) return 'a tap proof has no lead';
  if (!Array.isArray(lead) || lead.length < 1 || lead.length > VERB_PROOF_LEAD_MAX) return `lead must list 1..${VERB_PROOF_LEAD_MAX} held inputs`;
  for (const step of lead) {
    if (!step || typeof step !== 'object' || Object.keys(step).sort().join(',') !== 'input,ms'
      || !['left', 'right', 'up', 'down', 'a', 'b'].includes((step as VerbLeadV1).input)
      || !Number.isInteger((step as VerbLeadV1).ms) || (step as VerbLeadV1).ms < VERB_PROOF_MS.min || (step as VerbLeadV1).ms > VERB_PROOF_MS.max) {
      return `each lead step is { input: left|right|up|down|a|b, ms ${VERB_PROOF_MS.min}..${VERB_PROOF_MS.max} }`;
    }
  }
  return null;
}

export type VerbProofV1 = {
  verb: string;
  ms: number;
  /** Held inputs played, in order, before the verb (see {@link VerbLeadV1}). */
  lead?: VerbLeadV1[];
  /** `state-diverges`: for an observable that moves by itself too (a wind on
   * the seat): the held verb must leave it unlike the same match without the
   * verb at some tick of the hold. `state-change` also requires the value at
   * the end to differ from the value when the verb starts. Every kind is
   * credited only where it differs from the seed-matched match without the
   * verb at the same tick ({@link inputCausedDivergence}). */
  expect: { kind: 'event'; event: string } | { kind: 'state-change'; path: string } | { kind: 'state-diverges'; path: string };
} & ({ input: VerbInputV1 } | { input: 'tap'; target: string });

export interface TemplateDisclosureV1 {
  coreVerbs: string[];
  mechanics: string[];
  controls: string;
  winCondition: string;
}

export interface TemplateOverridesV1 {
  tunables?: Record<string, number>;
  /** Text slot path → replacement. */
  content?: Record<string, string>;
  /** Archetype kind renames (`mario` → `plumber`); references follow. */
  rename?: Record<string, string>;
  palette?: string[];
  /** What the game is made of beyond the template's (structure.ts); applied
   * first, before tunables, content and palette. */
  structure?: TemplateStructureV1;
  /** The game's own player-facing text (design summary + rules) when the
   * patch makes it a different game than the template's text describes:
   * kinds renamed or re-roled, structure changed (textFacts.ts
   * `rulesTextRequired`). Text only — it changes nothing that plays; its
   * numbers and nouns are checked against the game (`rulesTextProblems`). */
  rules?: TemplateRulesTextV1;
}

/** A game's own pitch and rules: the template's disclosure minus its core
 * verbs, which stay the template's (each one has a verb proof). */
export interface TemplateRulesTextV1 {
  pitch: string;
  mechanics: string[];
  controls: string;
  winCondition: string;
}

/** Bounds of a patch's rules text (inside the game design's own limits). */
export const TEMPLATE_RULES_LIMITS = Object.freeze({ pitchMax: 240, mechanicsMax: 12, mechanicMax: 300, controlsMax: 240, winConditionMax: 300 });

function segments(path: string): string[] {
  if (!path.startsWith('/')) throw new RangeError(`path must start with /: ${path}`);
  return path.slice(1).split('/');
}

/** Resolve a module-config path to its container + final key. */
function locate(spec: GameSpecV1, path: string): { parent: JsonObject | JsonValue[]; key: string } {
  const parts = segments(path);
  let node: JsonValue;
  let rest: string[];
  if (parts[0] === 'archetypes' && parts[2] === 'modules') {
    const archetype = spec.archetypes[parts[1]!];
    const use = archetype?.modules.find(m => m.use === parts[3]);
    if (!use) throw new RangeError(`no module ${parts[3]} on ${parts[1]}`);
    node = use.config; rest = parts.slice(4);
  } else if (parts[0] === 'globals') {
    const use = spec.globals.find((g: ModuleUseV1) => (g.key ?? g.use) === parts[1]);
    if (!use) throw new RangeError(`no global ${parts[1]}`);
    node = use.config; rest = parts.slice(2);
  } else {
    throw new RangeError(`tunable paths address module configs: ${path}`);
  }
  for (let i = 0; i < rest.length - 1; i++) {
    const next: JsonValue | undefined = Array.isArray(node) ? node[Number(rest[i])] : (node as JsonObject)[rest[i]!];
    if (next === undefined || next === null || typeof next !== 'object') throw new RangeError(`path ${path} does not resolve`);
    node = next;
  }
  if (node === null || typeof node !== 'object') throw new RangeError(`path ${path} does not resolve`);
  return { parent: node, key: rest[rest.length - 1]! };
}

export function readPath(spec: GameSpecV1, path: string): JsonValue | undefined {
  const { parent, key } = locate(spec, path);
  return Array.isArray(parent) ? parent[Number(key)] : parent[key];
}

function writePath(spec: GameSpecV1, path: string, value: JsonValue): void {
  const { parent, key } = locate(spec, path);
  if (Array.isArray(parent)) parent[Number(key)] = value; else parent[key] = value;
}

/** Rename archetype kinds everywhere they are referenced: the archetype map,
 * player seats, a derived kind's source, and each module config's declared references (every module
 * maps its own references, so a tag that happens to share a kind's name is
 * never touched). */
export function renameArchetypes(input: GameSpecV1, map: Record<string, string>,
  registry: ReadonlyMap<string, AnyComposeModule>): GameSpecV1 {
  const spec = structuredClone(input);
  for (const [from, to] of Object.entries(map)) {
    if (!spec.archetypes[from]) throw new RangeError(`no archetype ${from}`);
    if (spec.archetypes[to] && !map[to]) throw new RangeError(`archetype ${to} already exists`);
  }
  const renamed = (name: string) => map[name] ?? name;
  spec.archetypes = Object.fromEntries(Object.entries(spec.archetypes).map(([name, a]) => [renamed(name), a]));
  spec.players.archetypes = spec.players.archetypes.map(renamed);
  const rewrite = (use: ModuleUseV1) => {
    const module = registry.get(use.use);
    // Generated modules execute in a node:vm realm. Transfer their returned
    // config into this realm before it re-enters the strict JSON validator;
    // structuredClone also fails closed on any non-data result.
    if (module?.mapArchetypeRefs) use.config = structuredClone(module.mapArchetypeRefs(use.config, renamed));
  };
  for (const a of Object.values(spec.archetypes)) {
    a.modules.forEach(rewrite);
    if (a.render.derivedFrom !== undefined) a.render.derivedFrom = renamed(a.render.derivedFrom);
  }
  spec.globals.forEach(rewrite);
  return spec;
}

const TEXT_SLOT = /^\/(title|summary|presentation\/backgroundRole|archetypes\/[a-z][a-z0-9-]*\/role)$/;
/** A text field inside a module config (a placed tool's label, a ticket's
 * title): the path must resolve to an existing string. */
const CONFIG_TEXT_SLOT = /^\/(archetypes\/[a-z][a-z0-9-]*\/modules\/[a-z]+\.[a-zA-Z0-9]+|globals\/[a-z][a-zA-Z0-9.-]*)(\/[A-Za-z0-9_-]+)+$/;

function isTextSlotPath(spec: GameSpecV1, path: string): boolean {
  if (TEXT_SLOT.test(path)) return true;
  if (!CONFIG_TEXT_SLOT.test(path)) return false;
  try { return typeof readPath(spec, path) === 'string'; } catch { return false; }
}

function textAt(spec: GameSpecV1, path: string): string {
  if (path === '/title') return spec.title;
  if (path === '/summary') return spec.summary;
  if (path === '/presentation/backgroundRole') return spec.presentation.backgroundRole;
  if (TEXT_SLOT.test(path)) return spec.archetypes[segments(path)[1]!]!.role;
  return readPath(spec, path) as string;
}

/** The game a patch's paths name: the template after the patch's structure,
 * before its data and renames. An adaptation's structure can drop or reshape
 * a layout entry, a module or a kind, so every slot and tunable a patch may
 * write is read from this spec, never from the bare template. Throws when
 * the structure doesn't apply. */
export function structuredTemplateSpec(template: TemplateV1, structure?: TemplateStructureV1): GameSpecV1 {
  return structure ? applyStructure(template.spec, structure) : template.spec;
}

/** The template's tunables the game `spec` still has: each target (its
 * path and every mirror) is a number there. */
export function availableTunables(template: TemplateV1, spec: GameSpecV1 = template.spec): TunableV1[] {
  return template.tunables.filter(t => [t.path, ...(t.mirrors ?? [])].every(target => {
    try { return typeof readPath(spec, target) === 'number'; } catch { return false; }
  }));
}

/** Every concrete text slot of a template's game `spec` (by default the
 * template's own; pass {@link structuredTemplateSpec} for an adapted game)
 * with its current text: `*` expands over kinds (`/archetypes/*` + `/role`)
 * or array indices (a layout's labels), so a generator sees exactly the
 * paths it may write. */
export function contentSlotValues(template: TemplateV1, spec: GameSpecV1 = template.spec): Array<{ path: string; maxLength: number; value: string }> {
  const out: Array<{ path: string; maxLength: number; value: string }> = [];
  const expand = (prefix: string[], rest: string[], maxLength: number): void => {
    if (!rest.length) {
      const path = '/' + prefix.join('/');
      if (isTextSlotPath(spec, path)) out.push({ path, maxLength, value: textAt(spec, path) });
      return;
    }
    const [head, ...tail] = rest;
    if (head !== '*') { expand([...prefix, head!], tail, maxLength); return; }
    if (prefix.length === 1 && prefix[0] === 'archetypes') {
      for (const kind of Object.keys(spec.archetypes)) expand([...prefix, kind], tail, maxLength);
      return;
    }
    let container: unknown;
    try { container = readPath(spec, '/' + prefix.join('/')); } catch { return; }
    if (Array.isArray(container)) container.forEach((_, i) => expand([...prefix, String(i)], tail, maxLength));
  };
  for (const slot of template.contentSlots) expand([], segments(slot.path), slot.maxLength);
  return out;
}

function writeText(spec: GameSpecV1, path: string, value: string): void {
  if (!TEXT_SLOT.test(path)) { writePath(spec, path, value); return; }
  const parts = segments(path);
  if (parts[0] === 'title') spec.title = value;
  else if (parts[0] === 'summary') spec.summary = value;
  else if (parts[0] === 'presentation') spec.presentation.backgroundRole = value;
  else {
    const archetype = spec.archetypes[parts[1]!];
    if (!archetype) throw new RangeError(`no archetype ${parts[1]}`);
    archetype.role = value;
  }
}

function slotMatches(pattern: string, path: string): boolean {
  const a = segments(pattern), b = segments(path);
  return a.length === b.length && a.every((seg, i) => seg === '*' || seg === b[i]);
}

/** Apply bounded overrides to a template. Out-of-range tunables, unknown
 * paths and over-long text are rejected (never clamped). */
export function instantiateTemplate(template: TemplateV1, overrides: TemplateOverridesV1,
  registry: ReadonlyMap<string, AnyComposeModule>): GameSpecV1 {
  let spec = overrides.structure ? applyStructure(template.spec, overrides.structure) : structuredClone(template.spec);
  for (const [path, value] of Object.entries(overrides.tunables ?? {})) {
    const tunable = template.tunables.find(t => t.path === path);
    if (!tunable) throw new RangeError(`not a tunable: ${path}`);
    if (!Number.isFinite(value) || value < tunable.min || value > tunable.max || (tunable.integer && !Number.isInteger(value))) {
      throw new RangeError(`${path} must be ${tunable.integer ? 'an integer' : 'a number'} in [${tunable.min}, ${tunable.max}]`);
    }
    for (const target of [path, ...(tunable.mirrors ?? [])]) writePath(spec, target, value);
  }
  for (const [path, value] of Object.entries(overrides.content ?? {})) {
    const slot = template.contentSlots.find(s => slotMatches(s.path, path));
    if (!slot || !isTextSlotPath(spec, path)) throw new RangeError(`not a content slot: ${path}`);
    if (typeof value !== 'string' || !value.trim() || value.length > slot.maxLength) throw new RangeError(`${path} must be 1..${slot.maxLength} chars`);
    writeText(spec, path, value);
  }
  if (overrides.palette) {
    if (overrides.palette.length !== spec.presentation.palette.length) throw new RangeError('palette must keep its length');
    spec.presentation.palette = [...overrides.palette];
  }
  if (overrides.rename) spec = renameArchetypes(spec, overrides.rename, registry);
  return spec;
}

/** A patch's rules text, exactly shaped and bounded, or what is wrong with it. */
function validateRulesText(input: unknown): TemplateRulesTextV1 | string {
  const L = TEMPLATE_RULES_LIMITS;
  const shape = `rules: expected {"pitch": text ≤ ${L.pitchMax}, "mechanics": [1..${L.mechanicsMax} texts ≤ ${L.mechanicMax}], "controls": text ≤ ${L.controlsMax}, "winCondition": text ≤ ${L.winConditionMax}} of plain text`;
  if (!plainObject(input) || Object.keys(input).sort().join(',') !== 'controls,mechanics,pitch,winCondition') return shape;
  const text = (v: unknown, max: number) => typeof v === 'string' && !!v.trim() && v.trim() === v && v.length <= max && !UNSAFE_TEXT.test(v);
  const { pitch, mechanics, controls, winCondition } = input;
  if (!text(pitch, L.pitchMax) || !text(controls, L.controlsMax) || !text(winCondition, L.winConditionMax)
    || !Array.isArray(mechanics) || !mechanics.length || mechanics.length > L.mechanicsMax || !mechanics.every(m => text(m, L.mechanicMax))) return shape;
  return { pitch: pitch as string, mechanics: [...mechanics as string[]], controls: controls as string, winCondition: winCondition as string };
}

const PATCH_KEYS = ['tunables', 'content', 'rename', 'palette', 'structure', 'rules'] as const;
const KIND = /^[a-z][a-z0-9-]{0,39}$/;
const HEX = /^#[0-9a-f]{6}$/;
const UNSAFE_TEXT = /[\u0000-\u001f\u007f<>{}`]/;

function plainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && [Object.prototype, null].includes(Object.getPrototypeOf(value));
}

export type TemplatePatchValidationV1 =
  | { ok: true; patch: TemplateOverridesV1; spec: GameSpecV1 }
  | { ok: false; errors: string[] };

/** Validate an UNTRUSTED patch (a model reply, a wire acknowledgement)
 * against a template, collecting every error so a repairing generator sees
 * the whole list at once: closed fields, tunables in range, content slots by
 * pattern, renames to fresh kebab-case kinds, a same-length lowercase
 * palette; then the instantiated spec must validate. Paths always name the
 * TEMPLATE's original kinds (renames apply last). Values are never clamped. */
export function validateTemplatePatch(template: TemplateV1, input: unknown,
  registry: ReadonlyMap<string, AnyComposeModule>): TemplatePatchValidationV1 {
  const errors: string[] = [];
  if (!plainObject(input)) return { ok: false, errors: ['patch: expected an object'] };
  for (const key of Object.keys(input)) {
    if (!(PATCH_KEYS as readonly string[]).includes(key)) errors.push(`patch.${key}: unsupported field (allowed: ${PATCH_KEYS.join(', ')})`);
  }
  const patch: TemplateOverridesV1 = {};
  if (input.structure !== undefined) {
    const structure = validateStructure(input.structure, registry);
    if (!structure.ok) errors.push(...structure.errors);
    else if (structure.structure.verbProofs
      && structure.structure.verbProofs.map(v => v.verb).join('\n') !== template.disclosure.coreVerbs.join('\n')) {
      errors.push(`structure.verbProofs: restate one proof per core verb, in order (${template.disclosure.coreVerbs.join(', ')})`);
    } else if (structure.structure.ops.length || structure.structure.signature.length || structure.structure.events || structure.structure.verbProofs) {
      patch.structure = structure.structure;
    }
  }
  // Every path below names the game the patch's structure makes (an
  // adaptation can drop a layout entry, a module or a kind), and every bad
  // path is reported at once.
  let base = template.spec;
  if (patch.structure) {
    try { base = structuredTemplateSpec(template, patch.structure); }
    catch (e) { errors.push((e as Error).message); }
  }
  const tunablesHere = availableTunables(template, base);
  if (input.tunables !== undefined) {
    if (!plainObject(input.tunables)) errors.push('tunables: expected an object of path → number');
    else {
      const tunables: Record<string, number> = {};
      for (const [path, value] of Object.entries(input.tunables)) {
        const tunable = template.tunables.find(t => t.path === path);
        if (!tunable) { errors.push(`tunables["${path}"]: not a tunable of ${template.id}`); continue; }
        if (!tunablesHere.includes(tunable)) { errors.push(`tunables["${path}"]: not in this game (its structure removed what it tunes)`); continue; }
        if (typeof value !== 'number' || !Number.isFinite(value) || value < tunable.min || value > tunable.max
          || (tunable.integer && !Number.isInteger(value))) {
          errors.push(`tunables["${path}"]: must be ${tunable.integer ? 'an integer' : 'a number'} in [${tunable.min}, ${tunable.max}]`);
          continue;
        }
        tunables[path] = value;
      }
      patch.tunables = tunables;
    }
  }
  if (input.content !== undefined) {
    if (!plainObject(input.content)) errors.push('content: expected an object of path → text');
    else {
      const content: Record<string, string> = {};
      for (const [path, value] of Object.entries(input.content)) {
        const slot = template.contentSlots.find(s => slotMatches(s.path, path));
        if (!slot || !isTextSlotPath(base, path) || (path.startsWith('/archetypes/') && !base.archetypes[path.split('/')[2]!])) {
          errors.push(`content["${path}"]: not a content slot of ${template.id}${patch.structure ? ' as its structure leaves it' : ''}`); continue;
        }
        if (typeof value !== 'string' || !value.trim() || value.trim() !== value || value.length > slot.maxLength || UNSAFE_TEXT.test(value)) {
          errors.push(`content["${path}"]: must be 1..${slot.maxLength} chars of plain text`); continue;
        }
        content[path] = value;
      }
      patch.content = content;
    }
  }
  if (input.rename !== undefined) {
    if (!plainObject(input.rename)) errors.push('rename: expected an object of kind → new kind');
    else {
      const rename: Record<string, string> = {};
      const targets = new Set<string>();
      for (const [from, to] of Object.entries(input.rename)) {
        if (!base.archetypes[from]) { errors.push(`rename["${from}"]: no archetype ${from}`); continue; }
        if (typeof to !== 'string' || !KIND.test(to) || to === 'background') {
          errors.push(`rename["${from}"]: must be a kebab-case kind (never "background")`); continue;
        }
        if (targets.has(to) || (to !== from && base.archetypes[to] && !Object.hasOwn(input.rename, to))) {
          errors.push(`rename["${from}"]: kind ${to} is already taken`); continue;
        }
        targets.add(to);
        if (to !== from) rename[from] = to;
      }
      patch.rename = rename;
    }
  }
  // An empty palette list means "keep the template's colours", exactly as an
  // empty tunables/content/rename object changes nothing; it is not recorded.
  if (input.palette !== undefined && !(Array.isArray(input.palette) && input.palette.length === 0)) {
    const length = template.spec.presentation.palette.length;
    if (!Array.isArray(input.palette) || input.palette.length !== length
      || input.palette.some(c => typeof c !== 'string' || !HEX.test(c))) {
      errors.push(`palette: expected exactly ${length} lowercase #rrggbb colours (each index keeps its role)`);
    } else patch.palette = [...input.palette as string[]];
  }
  if (input.rules !== undefined) {
    const rules = validateRulesText(input.rules);
    if (typeof rules === 'string') errors.push(rules);
    else patch.rules = rules;
  }
  if (errors.length) return { ok: false, errors };
  let spec: GameSpecV1;
  try { spec = instantiateTemplate(template, patch, registry); }
  catch (e) { return { ok: false, errors: [(e as Error).message] }; }
  const checked = validateGameSpec(spec, registry);
  return checked.ok ? { ok: true, patch, spec: checked.spec } : { ok: false, errors: checked.errors };
}
